import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Chip,
  Grid,
  Autocomplete,
  Divider,
  Tooltip,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { ITemplate, ITemplateField } from '../types/damTypes';
import { sv } from 'date-fns/locale';
import { useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import ScienceIcon from '@mui/icons-material/Science';
import StraightenIcon from '@mui/icons-material/Straighten';
import CategoryIcon from '@mui/icons-material/Category';
import { styled } from '@mui/material/styles';
import AssetReferenceFieldInput from '../modules/referenceFields/AssetReferenceFieldInput';
import {
  formatAssetReferenceLabels,
  normalizeAssetReferenceValues,
} from '../modules/referenceFields/utils';
import {
  sanitizeSimpleTemplateHtml,
  shouldRenderSimpleTemplateHtml,
} from '../utils/templateFieldHtml';
import {
  getResolvedTemplateFieldValue,
  getUrlFieldValidationError,
  getVisibleTemplateFields,
} from '../utils/templateFieldValidation';
import {
  FullPageLayoutSection,
  groupFullPageLayoutSections,
  normalizeFullPageLayoutProfile,
  sortTemplateFieldsForDisplay,
  splitFieldsIntoFullPageSections,
} from '../modules/fullPageAssetDetail/layoutProfiles';

const ProductHeaderBox = styled(Box)(({ theme }) => ({
  padding: '24px 0',
  marginBottom: '16px'
}));

const MetaChip = styled(Chip)(({ theme }) => ({
  background: '#f7fafc',
  border: '1px solid #e2e8f0',
  fontSize: '0.8rem',
  color: '#4a5568',
  fontWeight: 400,
  height: 'auto',
  '& .MuiChip-label': {
    padding: '4px 12px'
  }
}));

const WORDPRESS_QUOTE_BACKGROUND_FIELD_ID = 'loizos_quote_background_image_recept_id';

export type TemplateFieldTransferPayload = {
  label: string;
  text: string;
  html?: string;
  kind?: 'heading' | 'body' | 'bullets';
};

interface TemplateFieldsFormProps {
  template: ITemplate | null;
  values: Record<string, any>;
  onChange: (fieldId: string, value: any) => void;
  disabled?: boolean;
  showUploadFields?: boolean; // Whether to show fields marked for upload
  showEditFields?: boolean;   // Whether to show fields marked for edit
  groupBySeparators?: boolean; // Optional: render fields grouped by separator as Accordions
  hideSeparators?: boolean; // NEW: hide separator fields (used in upload UI)
  layoutVariant?: 'panel' | 'fullPage';
  onValueDragStart?: (event: React.DragEvent, payload: TemplateFieldTransferPayload) => void;
}

const TemplateFieldsForm: React.FC<TemplateFieldsFormProps> = ({
  template,
  values,
  onChange,
  disabled = false,
  showUploadFields = true,
  showEditFields = true,
  groupBySeparators = false,
  hideSeparators = false,
  layoutVariant = 'panel',
  onValueDragStart,
}) => {
  const theme = useTheme();
  const isFullPageLayout = layoutVariant === 'fullPage';

  // Return early if no template provided
  if (!template) {
    console.log('TemplateFieldsForm: No template provided');
    return <Typography>No template selected</Typography>;
  }
  
  console.log('TemplateFieldsForm: Template received:', template);
  console.log('TemplateFieldsForm: Template fields:', template.fields);
  const isRecipeTemplate = String(template.name || '').trim().toLowerCase() === 'recept';
  
  // Filter fields based on showUploadFields and showEditFields
  const baseVisibleFields = getVisibleTemplateFields(template, {
    showUploadFields,
    showEditFields,
    hideSeparators,
  });
  
  console.log('TemplateFieldsForm: Base visible fields after filtering:', baseVisibleFields);

  const resolveFieldValue = (field: ITemplateField | undefined) => {
    if (!field) return '';

    const nameValue = field.name ? values[field.name] : undefined;
    let value = getResolvedTemplateFieldValue(field, values);

    if (field.type === 'checkbox') {
      const normalizeCheckboxValue = (val: any) => {
        if (val === true || val === false) return val;
        if (typeof val === 'string') {
          const normalized = val.trim().toLowerCase();
          if (['true', '1', 'yes'].includes(normalized)) return true;
          if (['false', '0', 'no'].includes(normalized)) return false;
        }
        return undefined;
      };

      const normalizedValue = normalizeCheckboxValue(value);
      const normalizedName = normalizeCheckboxValue(nameValue);

      if (normalizedValue === false && normalizedName === true) return true;
      if (normalizedValue !== undefined) return normalizedValue;
      if (normalizedName !== undefined) return normalizedName;
    }

    if (
      (String(field.id || '').trim().toLowerCase() === WORDPRESS_QUOTE_BACKGROUND_FIELD_ID
        || String(field.name || '').trim().toLowerCase() === WORDPRESS_QUOTE_BACKGROUND_FIELD_ID)
      && field.type === 'list'
    ) {
      if (Array.isArray(value)) {
        return value.map(item => String(item ?? '').trim()).find(Boolean) || '';
      }
      if (typeof value === 'string') {
        return value
          .split(',')
          .map(item => item.trim())
          .find(Boolean) || '';
      }
      return value === null || value === undefined ? '' : String(value).trim();
    }

    return value;
  };

  const isValueFilled = (val: any) => {
    if (val === null || val === undefined) return false;
    if (typeof val === 'string') return val.trim().length > 0;
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === 'number') return true;
    if (typeof val === 'boolean') return true;
    if (val instanceof Date) return true;
    if (typeof val === 'object') return Object.keys(val).length > 0;
    return Boolean(val);
  };

  const shouldHideEmptyFieldsInDisplay = disabled && template.hideEmptyFieldsInDisplay === true;

  const visibleFields = baseVisibleFields.filter(field => {
    if (field.type === 'separator') return true;
    if (!shouldHideEmptyFieldsInDisplay) return true;
    return isValueFilled(resolveFieldValue(field));
  });

  console.log('TemplateFieldsForm: Visible fields after display filtering:', visibleFields);
  
  if (visibleFields.length === 0) {
    console.log('TemplateFieldsForm: No fields to display after filtering');
    return disabled ? null : <Typography>No fields to display</Typography>;
  }

  const normalizeMultiValue = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value
        .map(item => String(item ?? '').trim())
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
    if (value === null || value === undefined || value === '') {
      return [];
    }
    return [String(value).trim()].filter(Boolean);
  };

  const normalizeBulletListValue = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value
        .map(item => String(item ?? '').trim())
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(/\r?\n/)
        .map(item => item.trim())
        .filter(Boolean);
    }
    if (value === null || value === undefined || value === '') {
      return [];
    }
    return [String(value).trim()].filter(Boolean);
  };

  const isRecipePermalinkField = (field: ITemplateField): boolean => {
    if (!isRecipeTemplate) return false;

    const id = String(field.id || '').trim().toLowerCase();
    const name = String(field.name || '').trim().toLowerCase();

    return id === 'permalink_recept_id' || name === 'permalink_recept_id';
  };

  const isQuoteBackgroundSelectField = (field: ITemplateField): boolean => {
    const id = String(field.id || '').trim().toLowerCase();
    const name = String(field.name || '').trim().toLowerCase();
    return id === WORDPRESS_QUOTE_BACKGROUND_FIELD_ID || name === WORDPRESS_QUOTE_BACKGROUND_FIELD_ID;
  };

  const getExternalLinkHref = (value: any): string => {
    const text = typeof value === 'string' ? value.trim() : '';
    return /^https?:\/\//i.test(text) ? text : '';
  };

  const getOptionDisplayLabel = (field: ITemplateField, optionValue: any) => {
    const normalizedValue = String(optionValue ?? '').trim();
    if (!normalizedValue) return '';
    return field.options?.find(opt => opt.value === normalizedValue)?.label || normalizedValue;
  };

  const formatFieldValue = (field: ITemplateField, value: any, emptyValue: string = '—') => {
    const effectiveType = isQuoteBackgroundSelectField(field) && field.type === 'list'
      ? 'select'
      : field.type;

    switch (effectiveType) {
      case 'list': {
        const valuesList = normalizeMultiValue(value);
        if (!valuesList.length) return emptyValue;
        return valuesList.map(optionValue => getOptionDisplayLabel(field, optionValue)).join(', ');
      }
      case 'bulletList': {
        const valuesList = normalizeBulletListValue(value);
        if (!valuesList.length) return emptyValue;
        return valuesList.join('\n');
      }
      case 'tags':
        return Array.isArray(value) ? (value.length ? value.join(', ') : emptyValue) : (value || emptyValue);
      case 'reference':
        return formatAssetReferenceLabels(value, emptyValue);
      case 'checkbox':
        return value === true ? 'Ja' : value === false ? 'Nej' : emptyValue;
      case 'date': {
        if (!value) return emptyValue;
        try {
          const d = value instanceof Date ? value : new Date(value);
          return d.toLocaleDateString('sv-SE');
        } catch {
          return String(value);
        }
      }
      case 'select':
        if (value === undefined || value === null || value === '') return emptyValue;
        return getOptionDisplayLabel(field, value);
      case 'textarea':
        return (value !== undefined && value !== null && value !== '') ? String(value) : emptyValue;
      case 'number':
      case 'url':
      case 'text':
      default:
        return (value !== undefined && value !== null && value !== '') ? String(value) : emptyValue;
    }
  };

  const isFullWidthField = (field: ITemplateField) =>
    field.type === 'textarea'
    || field.type === 'bulletList'
    || field.type === 'separator'
    || field.type === 'reference'
    || (field.type === 'list' && !isQuoteBackgroundSelectField(field));

  // Same compact field grid in both panel and full page: the full page gains
  // width by laying out section cards in masonry columns, not by widening fields.
  const getGridColumns = () => ({ xs: '1fr', md: '1fr 1fr' });

  const getFieldGridColumn = (field: ITemplateField) =>
    isFullWidthField(field) ? { xs: '1', md: '1 / -1' } : 'auto';

  const stripSimpleHtml = (html: string) => String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const wrapTransferable = (
    payload: TemplateFieldTransferPayload | null,
    node: React.ReactNode,
    options?: { align?: 'start' | 'center' },
  ) => {
    const text = String(payload?.text || '').trim();
    if (!onValueDragStart || !payload || !text || text === '—') return node;
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: options?.align === 'center' ? 'center' : 'flex-start',
        gap: 0.25,
        userSelect: 'text',
        width: options?.align === 'center' ? '100%' : undefined,
      }}>
        <Box
          component="span"
          draggable
          onDragStart={(event) => {
            event.stopPropagation();
            onValueDragStart(event, payload);
          }}
          title="Dra till sliden"
          sx={{
            display: 'inline-flex',
            mt: 0.15,
            color: '#94a3b8',
            cursor: 'grab',
            flexShrink: 0,
            '&:active': { cursor: 'grabbing' },
            '&:hover': { color: '#1B3967' },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box
          sx={{ flex: 1, minWidth: 0, userSelect: 'text', cursor: 'text' }}
          onDoubleClick={(event) => {
            const range = document.createRange();
            range.selectNodeContents(event.currentTarget);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
          }}
        >
          {node}
        </Box>
      </Box>
    );
  };
  
  // Group visible fields by their group property and render them
  const renderFieldGroups = () => {
    // Sort all visible fields by order and then by label
    const sortedFields = sortTemplateFieldsForDisplay(visibleFields);
    const fullPageLayoutProfile = normalizeFullPageLayoutProfile(template.fullPageLayoutProfile);

    // If grouping by separators, split into sections and render Accordions
    if (groupBySeparators) {
      type Section = Pick<FullPageLayoutSection, 'id' | 'title' | 'fields' | 'isFirstSection'>;
      let sections: Section[] = [];
      let currentSection: Section | undefined;
      let isFirstSection = true;

      if (isFullPageLayout) {
        sections = splitFieldsIntoFullPageSections(visibleFields, fullPageLayoutProfile);
      } else {
        sortedFields.forEach((field) => {
          if (field.type === 'separator') {
            // Push previous section if it has fields
            if (currentSection && currentSection.fields.length > 0) {
              sections.push(currentSection);
            }
            // Start a new section
            currentSection = {
              id: field.id,
              title: field.label || 'Sektion',
              fields: [],
              isFirstSection: isFirstSection
            };
            isFirstSection = false;
          } else {
            if (!currentSection) {
              currentSection = {
                id: 'leading-fields',
                title: 'Övrigt',
                fields: [],
                isFirstSection: false
              };
            }
            currentSection.fields.push(field);
          }
        });
        if (currentSection && currentSection.fields.length > 0) {
          sections.push(currentSection);
        }
      }

      const getFieldValue = (field: ITemplateField) => resolveFieldValue(field);

      const getSectionIcon = (title: string) => {
        const t = title.toLowerCase();
        if (t.includes('närings')) return <ScienceIcon fontSize="small" sx={{ mr: 1 }} />;
        if (t.includes('förpack') || t.includes('mått') || t.includes('dimension')) return <StraightenIcon fontSize="small" sx={{ mr: 1 }} />;
        if (t.includes('ingredien') || t.includes('innehåll')) return <LocalDiningIcon fontSize="small" sx={{ mr: 1 }} />;
        if (t.includes('kategori') || t.includes('tagg')) return <CategoryIcon fontSize="small" sx={{ mr: 1 }} />;
        return <InfoOutlinedIcon fontSize="small" sx={{ mr: 1 }} />;
      };

      const renderProductHeader = (section: Section) => {
        // Get all fields marked for product header display
        const headerFields = section.fields.filter((f: ITemplateField) => f.showInProductHeader === true);
        
        // If no fields are flagged, don't render header
        if (headerFields.length === 0) return null;

        const getFieldValue = (field: ITemplateField | undefined) => resolveFieldValue(field);

        // Find title field (first text field with 'produktnamn' or 'titel' in label, or first text field)
        const titleField = headerFields.find((f: ITemplateField) => 
          f.label?.toLowerCase().includes('produktnamn') || 
          f.label?.toLowerCase().includes('titel') || 
          f.id === 'title' || 
          f.name === 'title'
        ) || headerFields.find((f: ITemplateField) => f.type === 'text');

        // Find description field (textarea or field with 'beskrivning')
        const descField = headerFields.find((f: ITemplateField) => 
          f.type === 'textarea' ||
          f.label?.toLowerCase().includes('beskrivning') || 
          f.id === 'description' || 
          f.name === 'description'
        );

        // Remaining fields go into meta chips
        const metaFields = headerFields.filter((f: ITemplateField) => 
          f !== titleField && f !== descField
        );

        const title = getFieldValue(titleField);
        const description = getFieldValue(descField);
        const renderDescriptionHtml = descField && shouldRenderSimpleTemplateHtml(descField.type, description);
        const sanitizedDescriptionHtml = renderDescriptionHtml ? sanitizeSimpleTemplateHtml(description) : '';

        return (
          <ProductHeaderBox>
            {/* Product Title */}
            {titleField && wrapTransferable(
              title ? { label: titleField.label || 'Titel', text: String(title), kind: 'heading' } : null,
              <Typography 
                variant="h4" 
                sx={{ 
                  fontSize: '1.5rem',
                  fontWeight: 500,
                  color: '#1a202c',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}
              >
                {title || titleField.label}
              </Typography>,
              { align: 'center' },
            )}

            {/* Meta Chips */}
            {metaFields.length > 0 && (
              <Box sx={{ 
                display: 'flex',
                gap: '12px',
                marginBottom: '16px',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}>
                {metaFields.map((field: ITemplateField) => {
                  const val = formatFieldValue(field, getFieldValue(field), '');
                  if (!val) return null;
                  const chipLabel = disabled && field.hideLabelInDisplay ? val : `${field.label}: ${val}`;
                  return (
                    <MetaChip
                      key={field.id}
                      label={chipLabel}
                      draggable={Boolean(onValueDragStart)}
                      onDragStart={(event) => {
                        if (!onValueDragStart) return;
                        event.stopPropagation();
                        onValueDragStart(event, { label: field.label, text: chipLabel, kind: 'body' });
                      }}
                      onDoubleClick={async (event) => {
                        event.preventDefault();
                        try {
                          await navigator.clipboard.writeText(chipLabel);
                        } catch {
                          // Selection still works as fallback.
                        }
                      }}
                      title={onValueDragStart ? 'Dra till sliden eller dubbelklicka för att kopiera' : undefined}
                      sx={onValueDragStart ? {
                        cursor: 'grab',
                        userSelect: 'text',
                        '& .MuiChip-label': { userSelect: 'text' },
                      } : undefined}
                    />
                  );
                })}
              </Box>
            )}

            {/* Description */}
            {description && wrapTransferable(
              {
                label: descField?.label || 'Beskrivning',
                text: renderDescriptionHtml ? stripSimpleHtml(sanitizedDescriptionHtml) : String(description),
                html: renderDescriptionHtml ? `<p>${sanitizedDescriptionHtml}</p>` : undefined,
                kind: 'body',
              },
              <Box 
                sx={{ 
                  color: '#2d3748',
                  fontSize: '0.9rem',
                  lineHeight: 1.4,
                  textAlign: 'center',
                  '& b, & strong': {
                    fontWeight: 700,
                  },
                }}
                {...(renderDescriptionHtml
                  ? { dangerouslySetInnerHTML: { __html: sanitizedDescriptionHtml } }
                  : {})}
              >
                {!renderDescriptionHtml ? description : null}
              </Box>,
              { align: 'center' },
            )}
          </ProductHeaderBox>
        );
      };

      const renderSectionCard = (section: Section, idx: number) => (
              <Box 
                key={section.id || `${section.title}-${idx}`}
                sx={{ 
                  mb: 2,
                  background: 'white',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  ...(isFullPageLayout && {
                    breakInside: 'avoid',
                    WebkitColumnBreakInside: 'avoid',
                  })
                }}
              >
                {section.isFirstSection && (
                  <Box sx={{ p: 2, pb: 0 }}>
                    {renderProductHeader(section)}
                  </Box>
                )}
                <Accordion 
                  defaultExpanded 
                  expanded={section.isFirstSection ? !disabled : undefined}
                  sx={{ 
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-root': {
                      background: '#f8f9fa',
                      borderBottom: '1px solid #e2e8f0',
                      color: '#2d3748',
                      padding: '2px 16px !important',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      minHeight: '32px !important',
                      '&.Mui-expanded': {
                        minHeight: '32px !important'
                      }
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '4px 0 !important',
                      '&.Mui-expanded': {
                        margin: '4px 0 !important'
                      }
                    },
                    '& .MuiAccordionDetails-root': {
                      padding: '8px 16px 12px 16px !important'
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getSectionIcon(section.title)}
                      <Typography sx={{ fontWeight: 500 }}>
                        {section.title}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {(() => {
                      // Check if this section has individual nutrition fields
                      const isNutritionField = (f: ITemplateField) =>
                        (f as any).externalFieldPath && String((f as any).externalFieldPath).includes('_nutrition_');
                      const nutritionFields = section.fields.filter(isNutritionField);
                      const nonNutritionFields = section.fields.filter(f => !isNutritionField(f));

                      // If we have nutrition fields AND we're in view mode (disabled), render a table
                      if (nutritionFields.length > 0 && disabled) {
                        return (
                          <Box>
                            {/* Render non-nutrition fields normally (e.g. the aggregate string) */}
                            {nonNutritionFields.length > 0 && (
                              <Box sx={{ 
                                display: 'grid', 
                                gridTemplateColumns: getGridColumns(),
                                gap: 1,
                                mb: 2
                              }}>
                                {nonNutritionFields.map((field) => (
                                  <Box 
                                    key={field.id} 
                                    sx={{ gridColumn: getFieldGridColumn(field) }}
                                  >
                                    {renderField(field)}
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* Nutrition table */}
                            {wrapTransferable(
                              (() => {
                                const rows = nutritionFields.map((field) => {
                                  let val = values[field.id];
                                  if ((val === undefined || val === '') && field.name) val = values[field.name];
                                  if (val === undefined) val = field.defaultValue;
                                  const displayValue = (val !== undefined && val !== null && val !== '') ? String(val) : '';
                                  let label = field.label || '';
                                  label = label.replace(/^\[.*?\]\s*/, '').replace(/^Näring:\s*/i, '');
                                  const cleanLabel = label.replace(/^-\s*/, '');
                                  return displayValue ? `${cleanLabel}: ${displayValue}` : '';
                                }).filter(Boolean);
                                return rows.length
                                  ? { label: 'Näringsvärde', text: rows.join('\n'), kind: 'body' as const }
                                  : null;
                              })(),
                              <TableContainer sx={{ 
                              border: '1px solid #e2e8f0', 
                              borderRadius: '6px',
                              overflow: 'hidden'
                            }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f7fafc' }}>
                                    <TableCell sx={{ 
                                      fontWeight: 600, 
                                      fontSize: '0.75rem', 
                                      color: '#4a5568',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.3px',
                                      borderBottom: '2px solid #e2e8f0',
                                      py: 1
                                    }}>
                                      Näringsvärde
                                    </TableCell>
                                    <TableCell align="right" sx={{ 
                                      fontWeight: 600, 
                                      fontSize: '0.75rem', 
                                      color: '#4a5568',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.3px',
                                      borderBottom: '2px solid #e2e8f0',
                                      py: 1
                                    }}>
                                      Per 100 g
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {nutritionFields.map((field, nIdx) => {
                                    let val = values[field.id];
                                    if ((val === undefined || val === '') && field.name) val = values[field.name];
                                    if (val === undefined) val = field.defaultValue;
                                    const displayValue = (val !== undefined && val !== null && val !== '') ? String(val) : '—';
                                    
                                    // Clean up the label: remove "Näring: " prefix and "[Bas (Konsument)] " prefix if present
                                    let label = field.label || '';
                                    label = label.replace(/^\[.*?\]\s*/, '').replace(/^Näring:\s*/i, '');

                                    // Indent sub-values (starts with "- Varav")
                                    const isSubValue = label.startsWith('- Varav') || label.startsWith('Varav');
                                    const cleanLabel = isSubValue ? label.replace(/^-\s*/, '') : label;

                                    const rowText = displayValue !== '—' ? `${cleanLabel}: ${displayValue}` : '';
                                    return (
                                      <TableRow 
                                        key={field.id}
                                        draggable={Boolean(onValueDragStart && rowText)}
                                        onDragStart={(event) => {
                                          if (!onValueDragStart || !rowText) return;
                                          event.stopPropagation();
                                          onValueDragStart(event, { label: cleanLabel, text: rowText, kind: 'body' });
                                        }}
                                        sx={{ 
                                          '&:last-child td': { borderBottom: 0 },
                                          bgcolor: nIdx % 2 === 0 ? 'white' : '#fafbfc',
                                          cursor: onValueDragStart && rowText ? 'grab' : undefined,
                                          userSelect: 'text',
                                        }}
                                      >
                                        <TableCell sx={{ 
                                          fontSize: '0.85rem', 
                                          color: '#2d3748',
                                          py: 0.75,
                                          pl: isSubValue ? 4 : 2,
                                          fontStyle: isSubValue ? 'italic' : 'normal'
                                        }}>
                                          {cleanLabel}
                                        </TableCell>
                                        <TableCell align="right" sx={{ 
                                          fontSize: '0.85rem', 
                                          color: '#2d3748',
                                          py: 0.75,
                                          fontWeight: isSubValue ? 400 : 500
                                        }}>
                                          {displayValue}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            )}
                          </Box>
                        );
                      }

                      // Default: render all fields normally (edit mode or no nutrition fields)
                      return (
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: getGridColumns(),
                          gap: 1
                        }}>
                          {section.fields.map((field) => {
                            // Skip fields with redundant labels (e.g. "Näringsvärde" field inside "Näringsvärden" section)
                            const isRedundantLabel = field.label?.toLowerCase().includes('näringsvärde') && 
                                                   section.title?.toLowerCase().includes('näringsvärde');
                            
                            if (isRedundantLabel && disabled) {
                              // For redundant nutrition fields in view mode, just show the value without label
                              const value = formatFieldValue(field, resolveFieldValue(field), '');
                              if (!value) return null; // Skip empty redundant fields
                              
                              return (
                                <Box 
                                  key={field.id} 
                                  sx={{ 
                                    gridColumn: getFieldGridColumn(field),
                                    mb: 1
                                  }}
                                >
                                  {wrapTransferable(
                                    { label: field.label, text: value, kind: 'body' },
                                    <Typography sx={{ 
                                      color: '#2d3748', 
                                      fontSize: '0.9rem',
                                      lineHeight: 1.4
                                    }}>
                                      {value}
                                    </Typography>,
                                  )}
                                </Box>
                              );
                            }
                            
                            return (
                              <Box 
                                key={field.id} 
                                sx={{ 
                                  gridColumn: getFieldGridColumn(field)
                                }}
                              >
                                {renderField(field)}
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    })()}
                  </AccordionDetails>
                </Accordion>
              </Box>
      );

      if (isFullPageLayout) {
        const groupedSections = groupFullPageLayoutSections(
          sections as FullPageLayoutSection[],
          fullPageLayoutProfile,
        );

        if (['product', 'recipe', 'wine'].includes(fullPageLayoutProfile)) {
          const primarySections = groupedSections[0]?.sections || [];
          const secondarySections = groupedSections[1]?.sections || [];
          const hasSecondarySections = secondarySections.length > 0;

          return (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: '1fr',
                  xl: hasSecondarySections ? 'minmax(0, 2fr) minmax(0, 1fr)' : '1fr',
                },
                gap: 2,
                alignItems: 'start',
              }}
            >
              <Box
                sx={{
                  columnCount: { xs: 1, xl: hasSecondarySections ? 2 : 3 },
                  columnGap: 2,
                }}
              >
                {primarySections.map(renderSectionCard)}
              </Box>

              {hasSecondarySections && (
                <Box>
                  {secondarySections.map(renderSectionCard)}
                </Box>
              )}
            </Box>
          );
        }

        const orderedSections = groupedSections.flatMap((group) => group.sections);
        return (
          <Box
            sx={{
              // Generic fallback: section cards flow top-to-bottom per column.
              columnCount: { xs: 1, lg: 2, xl: 3 },
              columnGap: 2,
            }}
          >
            {orderedSections.map(renderSectionCard)}
          </Box>
        );
      }

      return (
        <Box>
          {sections.map(renderSectionCard)}
        </Box>
      );
    }

    return (
      <Box>
        {sortedFields.map((field) => {
          if (isFullWidthField(field)) {
            // Render textarea and separator fields full width
            return (
              <Box key={field.id} sx={{ mb: 2, width: '100%' }}>
                {renderField(field)}
              </Box>
            );
          } else {
            // Render other fields in a responsive layout
            return (
              <Box 
                key={field.id}
                sx={{ 
                  mb: 2,
                  width: { xs: '100%', md: '50%' },
                  display: 'inline-block',
                  pr: { xs: 0, md: 1 }
                }}
              >
                {renderField(field)}
              </Box>
            );
          }
        })}
      </Box>
    );
  };
  
  // Render field based on its type
  const renderField = (field: ITemplateField) => {
    const value = resolveFieldValue(field);
    const effectiveFieldType = isQuoteBackgroundSelectField(field) && field.type === 'list'
      ? 'select'
      : field.type;
    
    console.log(`Debug - Template field "${field.label}" (id: ${field.id}, name: ${field.name}): found value = ${value}, available values:`, Object.keys(values));
    const isRequired = field.validation?.required;
    // Check if the field should be disabled - either from the component prop or if it's marked as read-only
    const isDisabled = disabled || field.isReadOnly === true;
    const hideFieldLabelInDisplay = disabled && field.hideLabelInDisplay === true;
    
    // Helper function to handle field changes
    const handleFieldChange = (newValue: any) => {
      onChange(field.id, newValue);
    };
    
    // Function to render read-only indicator when field is read-only but form is not generally disabled
    const readOnlyIndicator = () => {
      if (field.isReadOnly && !disabled) {
        return (
          <Tooltip title="This field is read-only and cannot be edited">
            <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
          </Tooltip>
        );
      }
      return null;
    };
    
    // When disabled, render a clean, static view matching the spec
    if (isDisabled) {
      const formattedValue = formatFieldValue(field, value);
      const renderSimpleHtml = shouldRenderSimpleTemplateHtml(effectiveFieldType, value);
      const sanitizedHtml = renderSimpleHtml ? sanitizeSimpleTemplateHtml(value) : '';
      const externalLinkHref = (effectiveFieldType === 'url' || isRecipePermalinkField(field))
        ? getExternalLinkHref(value)
        : '';
      const bulletListItems = effectiveFieldType === 'bulletList'
        ? normalizeBulletListValue(value)
        : [];
      const valueNode = effectiveFieldType === 'bulletList' ? (
            bulletListItems.length > 0 ? (
              <Box
                component="ul"
                sx={{
                  color: '#2d3748',
                  fontSize: '0.85rem',
                  lineHeight: 1.35,
                  mt: hideFieldLabelInDisplay ? 0 : 0.2,
                  mb: 0,
                  pl: 2.4,
                }}
              >
                {bulletListItems.map((item, index) => (
                  <Box component="li" key={`${field.id}-${index}`} sx={{ mb: 0.2 }}>
                    {item}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{
                color: '#2d3748',
                fontSize: '0.85rem',
                lineHeight: 1.3,
                mt: hideFieldLabelInDisplay ? 0 : undefined
              }}>
                {formattedValue}
              </Typography>
            )
          ) : externalLinkHref ? (
            <Typography
              component="a"
              href={externalLinkHref}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#1d4ed8',
                fontSize: '0.85rem',
                lineHeight: 1.3,
                mt: hideFieldLabelInDisplay ? 0 : undefined,
                textDecoration: 'underline',
                wordBreak: 'break-word',
                '&:hover': {
                  color: '#1e40af',
                },
              }}
            >
              {String(value).trim()}
            </Typography>
          ) : renderSimpleHtml ? (
            <Box
              sx={{
                color: '#2d3748',
                fontSize: '0.85rem',
                lineHeight: 1.3,
                mt: hideFieldLabelInDisplay ? 0 : undefined,
                '& b, & strong': {
                  fontWeight: 700,
                },
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <Typography sx={{ 
              color: '#2d3748', 
              fontSize: '0.85rem',
              lineHeight: 1.3,
              mt: hideFieldLabelInDisplay ? 0 : undefined
            }}>
              {formattedValue}
            </Typography>
          );
      const transferText = effectiveFieldType === 'bulletList'
        ? bulletListItems.join('\n')
        : renderSimpleHtml
          ? stripSimpleHtml(sanitizedHtml)
          : externalLinkHref
            ? String(value).trim()
            : formattedValue;

      return (
        <Box sx={{ mb: 0.5 }}>
          {!hideFieldLabelInDisplay && (
            <Typography sx={{ 
              fontWeight: 600, 
              color: '#4a5568', 
              fontSize: '0.75rem', 
              mb: 0.2, 
              textTransform: 'uppercase', 
              letterSpacing: '0.3px' 
            }}>
              {field.label}
            </Typography>
          )}
          {wrapTransferable(
            {
              label: field.label,
              text: transferText,
              html: renderSimpleHtml ? `<p>${sanitizedHtml}</p>` : undefined,
              kind: effectiveFieldType === 'bulletList' ? 'bullets' : 'body',
            },
            valueNode,
          )}
          <Box sx={{ 
            height: '1px', 
            background: '#e2e8f0', 
            mt: 0.3,
            mb: 0.2
          }} />
        </Box>
      );
    }

    switch (effectiveFieldType) {
      case 'text':
      case 'url': {
        const urlValidationError = effectiveFieldType === 'url' ? getUrlFieldValidationError(value) : '';

        return (
          <TextField
            type={effectiveFieldType === 'url' ? 'url' : 'text'}
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                {field.label}
                {field.isReadOnly && !disabled && (
                  <Tooltip title="This field is read-only and cannot be edited">
                    <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                  </Tooltip>
                )}
              </Box>
            }
            value={value || ''}
            onChange={(e) => handleFieldChange(e.target.value)}
            placeholder={field.placeholder || (effectiveFieldType === 'url' ? 'https://example.com' : undefined)}
            fullWidth
            variant="outlined"
            size="small"
            disabled={isDisabled}
            required={isRequired}
            error={Boolean(urlValidationError) || (isRequired && (!value || value === ''))}
            helperText={urlValidationError || field.description}
            inputProps={effectiveFieldType === 'url' ? { autoComplete: 'url' } : undefined}
            InputLabelProps={{
              shrink: true,
              sx: {
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#4a5568',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }
            }}
            sx={{
              mb: 0.5,
              '& .MuiInputBase-root': {
                fontSize: '0.85rem'
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.7rem',
                mt: 0.3,
                mb: 0
              }
            }}
            InputProps={field.isReadOnly && !disabled ? {
              endAdornment: (
                <InputAdornment position="end">
                  <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                </InputAdornment>
              )
            } : undefined}
          />
        );
      }
        
      case 'textarea':
        return (
          <TextField
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                {field.label}
                {field.isReadOnly && !disabled && (
                  <Tooltip title="This field is read-only and cannot be edited">
                    <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                  </Tooltip>
                )}
              </Box>
            }
            value={value || ''}
            onChange={(e) => handleFieldChange(e.target.value)}
            placeholder={field.placeholder}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            rows={3}
            disabled={isDisabled}
            required={isRequired}
            error={isRequired && (!value || value === '')}
            helperText={field.description}
            InputLabelProps={{ 
              shrink: true,
              sx: { 
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#4a5568',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }
            }}
            sx={{
              mb: 0.5,
              '& .MuiInputBase-root': {
                fontSize: '0.85rem'
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.7rem',
                mt: 0.3,
                mb: 0
              }
            }}
            InputProps={field.isReadOnly && !disabled ? {
              endAdornment: (
                <InputAdornment position="end">
                  <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                </InputAdornment>
              )
            } : undefined}
          />
        );

      case 'bulletList': {
        const bulletListText = normalizeBulletListValue(value).join('\n');

        return (
          <TextField
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                {field.label}
                {field.isReadOnly && !disabled && (
                  <Tooltip title="This field is read-only and cannot be edited">
                    <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                  </Tooltip>
                )}
              </Box>
            }
            value={bulletListText}
            onChange={(e) => handleFieldChange(
              e.target.value
                .split(/\r?\n/)
                .map(item => item.trim())
                .filter(Boolean)
            )}
            placeholder={field.placeholder || 'En punkt per rad'}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            rows={4}
            disabled={isDisabled}
            required={isRequired}
            error={isRequired && normalizeBulletListValue(value).length === 0}
            helperText={field.description || 'Skriv en punkt per rad'}
            InputLabelProps={{ 
              shrink: true,
              sx: { 
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#4a5568',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }
            }}
            sx={{
              mb: 0.5,
              '& .MuiInputBase-root': {
                fontSize: '0.85rem'
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.7rem',
                mt: 0.3,
                mb: 0
              }
            }}
            InputProps={field.isReadOnly && !disabled ? {
              endAdornment: (
                <InputAdornment position="end">
                  <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                </InputAdornment>
              )
            } : undefined}
          />
        );
      }
        
      case 'number':
        return (
          <TextField
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                {field.label}
                {field.isReadOnly && !disabled && (
                  <Tooltip title="This field is read-only and cannot be edited">
                    <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                  </Tooltip>
                )}
              </Box>
            }
            type="number"
            value={value || ''}
            onChange={(e) => handleFieldChange(e.target.value)}
            placeholder={field.placeholder}
            fullWidth
            variant="outlined"
            size="small"
            disabled={isDisabled}
            required={isRequired}
            error={isRequired && (value === undefined || value === null || value === '')}
            helperText={field.description}
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max
            }}
            InputLabelProps={{ 
              shrink: true,
              sx: { 
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#4a5568',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }
            }}
            sx={{
              mb: 0.5,
              '& .MuiInputBase-root': {
                fontSize: '0.85rem'
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.7rem',
                mt: 0.3,
                mb: 0
              }
            }}
            InputProps={field.isReadOnly && !disabled ? {
              endAdornment: (
                <InputAdornment position="end">
                  <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                </InputAdornment>
              )
            } : undefined}
          />
        );
        
      case 'date':
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
            <Box sx={{ position: 'relative' }}>
              <DatePicker
                label={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    {field.label}
                    {field.isReadOnly && !disabled && (
                      <Tooltip title="This field is read-only and cannot be edited">
                        <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                      </Tooltip>
                    )}
                  </Box>
                }
                value={value || null}
                onChange={(date) => handleFieldChange(date)}
                slotProps={{ 
                  textField: { 
                    fullWidth: true, 
                    variant: 'outlined',
                    size: 'small',
                    disabled: isDisabled,
                    required: isRequired,
                    error: isRequired && !value,
                    helperText: field.description,
                    InputLabelProps: { 
                      shrink: true,
                      sx: { 
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#4a5568',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px'
                      }
                    },
                    sx: {
                      mb: 0.5,
                      '& .MuiInputBase-root': {
                        fontSize: '0.85rem'
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.7rem',
                        mt: 0.3,
                        mb: 0
                      }
                    },
                    InputProps: field.isReadOnly && !disabled ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                        </InputAdornment>
                      )
                    } : undefined
                  } 
                }}
              />
            </Box>
          </LocalizationProvider>
        );
        
      case 'select':
        return (
          <FormControl 
            fullWidth 
            variant="outlined"
            size="small"
            disabled={isDisabled}
            required={isRequired}
            error={isRequired && (!value || value === '')}
            sx={{ mb: 0.5 }}
          >
            <InputLabel 
              id={`field-select-${field.id}`} 
              shrink={true}
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#4a5568',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}
            >
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                {field.label}
                {field.isReadOnly && !disabled && (
                  <Tooltip title="This field is read-only and cannot be edited">
                    <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                  </Tooltip>
                )}
              </Box>
            </InputLabel>
            <Select
              labelId={`field-select-${field.id}`}
              value={value || ''}
              onChange={(e) => handleFieldChange(e.target.value)}
              label={field.label}
              sx={{
                '& .MuiSelect-select': {
                  fontSize: '0.85rem'
                }
              }}
              endAdornment={field.isReadOnly && !disabled ? (
                <InputAdornment position="end">
                  <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6, mr: 2 }} />
                </InputAdornment>
              ) : undefined}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {field.description && (
              <Typography 
                variant="caption" 
                color="textSecondary"
                sx={{ 
                  fontSize: '0.7rem',
                  mt: 0.3,
                  display: 'block'
                }}
              >
                {field.description}
              </Typography>
            )}
          </FormControl>
        );
      case 'list': {
        const availableOptions = field.options || [];
        const selectedValues = normalizeMultiValue(value);
        const selectedOptions = selectedValues.map(selectedValue =>
          availableOptions.find(option => option.value === selectedValue) || {
            value: selectedValue,
            label: getOptionDisplayLabel(field, selectedValue)
          }
        );

        return (
          <FormControl
            fullWidth
            margin="normal"
            disabled={isDisabled}
            required={isRequired}
            error={isRequired && selectedValues.length === 0}
          >
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={availableOptions}
              value={selectedOptions}
              onChange={(_, newValue) => handleFieldChange(newValue.map(option => option.value))}
              disabled={isDisabled}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    size="small"
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  {option.label}
                </li>
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={option.label}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      {field.label}
                      {field.isReadOnly && !disabled && (
                        <Tooltip title="This field is read-only and cannot be edited">
                          <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                        </Tooltip>
                      )}
                    </Box>
                  }
                  placeholder={field.placeholder || 'Select one or more options'}
                  helperText={field.description}
                  variant="outlined"
                  error={isRequired && selectedValues.length === 0}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {field.isReadOnly && !disabled && (
                          <InputAdornment position="end">
                            <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </FormControl>
        );
      }
        
      case 'checkbox':
        return (
          <FormControl 
            component="fieldset" 
            sx={{ mb: 0.5 }}
            disabled={isDisabled}
            required={isRequired}
            error={isRequired && value !== true}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true}
                  onChange={(e) => handleFieldChange(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box component="span" sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#4a5568',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px'
                }}>
                  {field.label}
                  {field.isReadOnly && !disabled && (
                    <Tooltip title="This field is read-only and cannot be edited">
                      <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                    </Tooltip>
                  )}
                </Box>
              }
            />
            {field.description && (
              <Typography 
                variant="caption" 
                color="textSecondary"
                sx={{ 
                  fontSize: '0.7rem',
                  mt: 0.3,
                  display: 'block'
                }}
              >
                {field.description}
              </Typography>
            )}
          </FormControl>
        );
        
      case 'tags':
        return (
          <FormControl 
            fullWidth 
            margin="normal"
            disabled={isDisabled}
            required={isRequired}
            error={isRequired && (!value || !value.length)}
          >
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={value || []}
              onChange={(_, newValue) => handleFieldChange(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      {field.label}
                      {field.isReadOnly && !disabled && (
                        <Tooltip title="This field is read-only and cannot be edited">
                          <LockIcon fontSize="small" color="action" sx={{ ml: 1, opacity: 0.6 }} />
                        </Tooltip>
                      )}
                    </Box>
                  }
                  placeholder={field.placeholder || "Type and press enter to add tags"}
                  helperText={field.description}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {field.isReadOnly && !disabled && (
                          <InputAdornment position="end">
                            <LockIcon fontSize="small" color="action" sx={{ opacity: 0.6 }} />
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </FormControl>
        );

      case 'reference':
        return (
          <Box sx={{ mt: 2 }}>
            <AssetReferenceFieldInput
              label={field.label}
              placeholder={field.placeholder || 'Sök och välj referenser'}
              helperText={field.description}
              value={normalizeAssetReferenceValues(value)}
              onChange={handleFieldChange}
              disabled={isDisabled}
              required={!!isRequired}
              config={field.referenceConfig}
            />
          </Box>
        );
        
      case 'separator':
        return (
          <Box sx={{ 
            my: 2,
            display: 'flex',
            alignItems: 'center',
            width: '100%'
          }}>
            <Divider sx={{ flexGrow: 1, mr: 1.5 }} />
            <Typography 
              variant="subtitle2" 
              color="text.secondary" 
              sx={{ 
                fontWeight: 'medium',
                px: 1.5,
                py: 0.5,
                bgcolor: '#f8f9fa',
                borderRadius: 0.5,
                border: '1px solid #e9ecef',
                fontSize: '0.875rem'
              }}
            >
              {field.label}
            </Typography>
            <Divider sx={{ flexGrow: 1, ml: 1.5 }} />
          </Box>
        );
        
      default:
        return (
          <Typography color="error">
            Unknown field type: {field.type}
          </Typography>
        );
    }
  };
  
  return (
    <Box>
      {renderFieldGroups()}
    </Box>
  );
};

export default TemplateFieldsForm; 